import { useEffect, useState } from "preact/hooks";
import { authPost } from "../../api/client.ts";
import type { AuthStep } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { location, setQuery } from "../../lib/router.ts";
import { Button } from "../../ui/Button.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";
import { authPath, fieldError, MIN_PASSWORD, next, useSubmit } from "./flow.ts";
import { AuthFrame, AuthLink, FormError } from "./frame.tsx";

/** Asking for a password reset link by email (the answer is the same whether or not the email has an account). */
export function ForgotPassword() {
  const [email, setEmail] = useState(location.value.searchParams.get("email") ?? "");
  const [sent, setSent] = useState("");
  const send = useSubmit(async () => {
    await authPost("/password-reset", { email });
    setSent(email.trim());
  });
  return (
    <AuthFrame
      title={t("auth.forgot.title")}
      lede={sent ? undefined : t("auth.forgot.lede")}
      footer={<AuthLink href={authPath("/signin", { email })}>{t("auth.backToSignIn")}</AuthLink>}
    >
      {sent ? (
        <p role="status" class="rounded-md bg-green-50 px-4 py-3 text-sm text-green-800">
          {t("auth.forgot.sent", { email: sent })}
        </p>
      ) : (
        <>
          <FormError error={send.error} />
          <Form class="flex flex-col gap-4" onSubmit={send.submit}>
            <Field label={t("auth.email")} error={fieldError(send.error, "email")}>
              <Input type="email" autocomplete="email" required={true} value={email} onValue={setEmail} />
            </Field>
            <Button variant="primary" submit={true} busy={send.busy} class="w-full">
              {t("auth.forgot.submit")}
            </Button>
          </Form>
        </>
      )}
    </AuthFrame>
  );
}

/** The reset link from the email (`?token=…`): a new password, then signed in with it. */
export function ResetPassword() {
  const [token] = useState(() => location.value.searchParams.get("token") ?? "");
  // Out of the address bar (and the history) once read.
  useEffect(() => setQuery({ token: null }), []);
  const [password, setPassword] = useState("");
  const reset = useSubmit(async () => next(await authPost<AuthStep>("/password-reset/confirm", { token, password })));
  const again = <AuthLink href={authPath("/forgot-password")}>{t("auth.reset.again")}</AuthLink>;
  return (
    <AuthFrame title={t("auth.reset.title")} lede={token ? t("auth.reset.lede") : undefined} footer={again}>
      {token ? (
        <>
          <FormError error={reset.error} />
          <Form class="flex flex-col gap-4" onSubmit={reset.submit}>
            <Field
              label={t("auth.password")}
              hint={t("auth.passwordHint", { min: MIN_PASSWORD })}
              error={fieldError(reset.error, "password")}
            >
              <Input
                type="password"
                autocomplete="new-password"
                required={true}
                minLength={MIN_PASSWORD}
                value={password}
                onValue={setPassword}
              />
            </Field>
            <Button variant="primary" submit={true} busy={reset.busy} class="w-full">
              {t("auth.reset.submit")}
            </Button>
          </Form>
        </>
      ) : (
        <p role="alert" class="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
          {t("auth.reset.noToken")}
        </p>
      )}
    </AuthFrame>
  );
}
